const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type FetchWithRetryOptions = RequestInit & {
  maxRetries?: number;
  retryDelay?: number;
  retryOnStatus?: number[];
};

const shouldRetry = (error: unknown, status?: number, retryOnStatus?: number[]): boolean => {
  if (error instanceof TypeError) {
    return true;
  }

  if (status && retryOnStatus?.includes(status)) {
    return true;
  }

  return false;
};

export const fetchWithRetry = async (
  url: string,
  options: FetchWithRetryOptions = {},
): Promise<Response> => {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    retryOnStatus = [
      500,
      502,
      503,
      504,
    ],
    ...fetchOptions
  } = options;

  let lastError: unknown;
  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      const response = await fetch(url, fetchOptions);

      if (!response.ok && shouldRetry(null, response.status, retryOnStatus) && attempt < maxRetries) {
        lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
        attempt++;
        await sleep(retryDelay * Math.pow(2, attempt - 1));
        continue;
      }

      return response;
    } catch (error) {
      lastError = error;

      if (shouldRetry(error) && attempt < maxRetries) {
        attempt++;
        await sleep(retryDelay * Math.pow(2, attempt - 1));
        continue;
      }

      throw error;
    }
  }

  // Safety net: should be unreachable, but guarantees a throw if loop logic changes
  throw lastError;
};
